<template>
  <div>
    <!-- Search -->
    <div class="row mt-4">
      <div class="col-md-6">
        <input
          class="input form-control"
          placeholder="Pesquisar por CNPJ"
          @keypress.enter="find()"
          v-model="query.generic"
        >
        <search-icon @click="find()" class="icon icon-within"/>
      </div>

      <div class="col-md-6">
        <button
          class="btn btn-outline px-3 mr-2"
          :class="{ 'btn-outline-active': showFilter }"
          @click="showFilter = !showFilter"
        >
          <span>Filtros</span>
          <chevron-down-icon
            class="fa-angle-right"
            :class="{'rotate-90': showFilter }"
          />
        </button>

        <button v-if="changeQuery" @click="find()" class="btn btn-link">
          Aplicar filtros ({{ numberOfBadges }})
        </button>
      </div>
    </div>

    <!-- Badges -->
    <div class="row">
      <div class="col-md-12">
        <BadgeFilters
          :filters="badgesFilters"
          :query="query"
          :amountChanged="updateNumberOfBadges"
          @clearAllBadges="$nextTick(() => find())"
          @clearBadge="$nextTick(() => find())"
        />
      </div>
    </div>

    <!-- Filter -->
    <template v-if="showFilter">
      <div class="row mt-3 mb-5">
        <div class="col-md-6">
          <label>Órgão consultado</label>
          <Select2 :options="filterServiceNames" v-model="query.scrapingServiceName"/>
        </div>

        <div class="col-md-6">
          <label>Status</label>
          <Select2 :options="filterStatus" v-model="query.status"/>
        </div>
      </div>

      <div class="row mt-3 mb-5">
        <div class="col-md-4">
          <label for="type-period">Tipo do período</label>
          <select v-model="query.periodType" id="type-period" class="browser-default custom-select">
            <option value="updated_at">Data de atualização</option>
            <option value="created_at">Data de criação</option>
          </select>
        </div>

        <div class="col-md-4">
          <label for="start-date">Data inicial</label>
          <input v-model="query.startDate" type="date" id="start-date" class="form-control">
        </div>

        <div class="col-md-4">
          <label for="end-date">Data final</label>
          <input v-model="query.endDate" type="date" id="end-date" class="form-control">
        </div>
      </div>
    </template>

    <hr>

    <!-- Register infos -->
    <div class="row mt-3">
      <div class="col-md-12">
        <h5><strong>{{ infoTotalModels }}</strong></h5>
      </div>
    </div>

    <!-- Table -->
    <div class="row mt-3">
      <div class="col-md-12">
        <table class="table table-borderless table-border table-align">
          <thead>
            <tr>
              <th>Serviço</th>
              <th>CNPJ</th>
              <th>Status</th>
              <th>Descrição</th>
              <th>Criado por</th>
              <th>Criado em</th>
              <th>Atualizado em</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="consult in consults" :key="consult.id">
              <td>{{ scrapingServiceName(consult.input.scrapingServiceName) }}</td>
              <td>{{ consult.input.cnpj | cnpj }}</td>
              <td>{{ translateStatus(consult.status) }}</td>
              <td>{{ buildDescription(consult) }}</td>
              <td>{{ consult.createdBy }}</td>
              <td>{{ consult.createdAt | datetime }}</td>
              <td>{{ consult.updatedAt | datetime }}</td>
            </tr>
          </tbody>
        </table>
        
        <div class="row">
          <div class="col-md-12 my-1">
            <Pagination
              v-model="meta.currentPage"
              :total-count="meta.totalCount"
              :total-pages="meta.totalPages"
              @pagination="pagination"
            />
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
  import BadgeFilters from '@/components/shares/BadgeFilters'
  import EntityProxy from '@/models/EntityProxy'
  import handleSearchable from '@/models/handleSearchable'
  import ModelMapper from '@/mixins/ModelMapper'
  import Pagination from '@/components/shares/specialTag/Pagination'
  import Select2 from '../shares/Select2.vue'

  import { ChevronDownIcon, SearchIcon } from 'vue-feather-icons'
  import { Filter } from '@/models/filter'

  const handleUserErrors = (consult) => {
    if (consult.status != '400') return ''

    const errors = consult.result?.errors || []
    const messages = errors.filter(e => e.category === 'client_error').map(e => e.message)
    return messages
  }

  export default {
    components: { BadgeFilters, ChevronDownIcon, Pagination, SearchIcon, Select2 },
    computed: {
      badgesFilters() {
        return [
          this.filterServiceNames,
          this.filterEndDate,
          this.filterStartDate,
          this.filterStatus,
          this.filterPeriodType
        ]
      },
      consults() {
        return this.getModels || []
      }
    },
    created() {
      this.$set(this.query, 'periodType', 'created_at')

      this.order.createdAt = 'desc'
      this.defaultModelSetting.updateQueryURL = true
      this.asyncTemplateSelect({
        byURL: true,
        pagination: true
      })

      this.fetchScrapingServices()
    },
    data: () => ({
      filterEndDate: new Filter({
        key: 'endDate',
        label: 'Data final',
        type: 'date'
      }),
      filterServiceNames: null,
      filterStartDate: new Filter({
        key: 'startDate',
        label: 'Data inicial',
        type: 'date'
      }),
      filterStatus: new Filter({
        key: 'status',
        label: 'Status',
        table: [
          ['Processado', 200],
          ['Em processo', 100],
          ['Erro do usuário', 400],
        ]
      }),
      scrapingServices: [],
      modelPathPrefix: 'cm-embarcador',
      nameModel: 'Consult',
      showDetailModal: true
    }),
    methods: {
      buildParams(pagination) {
        if (this.query.generic)
          this.query.generic = handleSearchable(this.query.generic)

        const params = ModelMapper.methods.buildParams.call(this, pagination)
        if (params.q?.status && Array.isArray(params.q.status) && params.q.status.includes(100) && !params.q.status.includes(500))
          params.q.status = [...params.q.status, 500]
        return params
      },
      buildFilterServiceNames() {
        this.filterServiceNames = new Filter({
          key: 'scrapingServiceName',
          label: 'Órgão consultado',
          table: this.scrapingServices.map(s => [s.friendlyName || s.name, s.name])
        })
      },
      buildDescription(consult) {
        if (consult.status == '200')
          return 'Consulta processada com sucesso'
        if (consult.status == '500')
          return 'Consulta em processamento'

        const userErrors = handleUserErrors(consult)
        if (userErrors.length > 0)
          return userErrors.join(', ')
        return ''
      },
      fetchScrapingServices() {
        const params = {
          meta: false,
          per_page: 10000,
          query: {
            enable: true,
            app_name: 'cm-embarcador'
          }
        }

        return this.axios.get('maestro/scraping_services', { params })
          .then(response => this.scrapingServices = EntityProxy.build(response.data))
          .then(() => this.buildFilterServiceNames())
          .catch(e => this.$notify.addError(e))
      },
      find() {
        this.asyncTemplateSelect()
      },
      translateStatus(value) {
        return {
          '500': 'Em processo',
          '400': 'Erro do usuário',
          '300': 'Erro',
          '200': 'Processado',
          '100': 'Em processo',
        }[value] || 'Não inicializado'
      },
      scrapingServiceName(scrapingServiceName) {
        const service = this.scrapingServices.find(s => s.name === scrapingServiceName)
        if (service)
          return service.friendlyName || service.name
        else
          return scrapingServiceName
      }
		},
		mixins: [ModelMapper],
    name: 'Consult',
  }
</script>

<style scoped>
  .btn {
    min-width: 113px;
  }
  
  strong {
    color:#465059;
    font-size: 14px;
  }
</style>
